import type { ReservationValidation } from '@/data/models/reservation.model';

/**
 * `confidence` / `confidenceScore` are optional on the /validate contract. When we POST
 * without them (because /score/trip-leg hasn't responded yet, or failed), the backend may
 * still return `fieldName: "confidence"` errors — those are spurious from the form's POV
 * and shouldn't surface as field errors. Drop them only when the matching leg in the
 * request actually omitted the confidence data.
 */
export function filterConfidenceErrorsForUnsetPayload(
  errors: ReservationValidation[],
  payload: unknown,
): ReservationValidation[] {
  return errors.filter((e) => !shouldDropConfidenceError(e, payload));
}

function shouldDropConfidenceError(
  error: ReservationValidation,
  payload: unknown,
): boolean {
  if ((error.fieldName ?? '').trim().toLowerCase() !== 'confidence') {
    return false;
  }
  if (error.tripLegSequence == null) return false;
  if (typeof payload !== 'object' || payload === null) return false;

  const tripLegs = (payload as { tripLegs?: unknown[] }).tripLegs;
  if (!Array.isArray(tripLegs)) return false;

  const leg = tripLegs[error.tripLegSequence - 1];
  if (typeof leg !== 'object' || leg === null) return false;

  const legObj = leg as Record<string, unknown>;
  if (legObj.confidence == null) return true;

  const pickup = legObj.pickUpAddress as Record<string, unknown> | undefined;
  if (!hasConfidenceScoreKeys(pickup?.confidenceScore)) return true;

  const dropoff = legObj.dropOffAddress as Record<string, unknown> | undefined;
  if (!hasConfidenceScoreKeys(dropoff?.confidenceScore)) return true;

  return false;
}

function hasConfidenceScoreKeys(value: unknown): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    Object.keys(value as object).length > 0
  );
}
